/* eslint-disable import/no-cycle */
import { defineStore } from 'pinia';
import Parse from '@/store/parseInitialization';
import {
  convertDateStringToISO,
  calculateSum,
  calculateAverage,
  getHoursBetween,
  updateChartDataByDay,
  updateChartDataByHour,
} from '@/script/index';
import { useAppStore } from './app';

export const usePatientStore = defineStore('patient', {
  state: () => ({
    app: useAppStore(),
    list: [],
    total: 0,
    dailyTests: 0,
    engagement: 0,
    // individual
    data: {},
    devices: [],
    stepMetrics: {
      numberSteps: 0,
      avgStepTime: 0,
      cadence: 0,
      distance: 0,
    },
    chartData: {
      labels: [],
      datasets: [],
    },
    error: {
      message: '',
      code: 0,
    },
  }),
  actions: {
    async getPatientsByDoctor() {
      const currentUser = Parse.User.current();
      if (!currentUser) {
        throw new Error('User not logged in');
      }

      const sessionToken = currentUser.getSessionToken();

      try {
        // retrieve all patients by doctor sessionToken
        const patients = await Parse.Cloud.run('patientsByDoctor', {}, { sessionToken });

        this.list = patients;
        this.total = patients.length;
        this.engagement = patients.reduce((t, n) => t + n.engagement, 0) / this.total;
      } catch ({ message }) {
        this.app.raiseAlert(message);
      }
    },
    async getPatientById(patientId) {
      const currentUser = Parse.User.current();
      if (!currentUser) {
        throw new Error('User not logged in');
      }

      const sessionToken = currentUser.getSessionToken();

      try {
        // retrieve patient by Id
        this.data = await Parse.Cloud.run('patientById', { patientId }, { sessionToken });
      } catch ({ code, message }) {
        this.error.code = code;
        this.error.message = message;
      }
    },
    async getStepsByDevicesPatient(patientId, start, end) {
      const currentUser = Parse.User.current();
      if (!currentUser) {
        throw new Error('User not logged in');
      }
      const sessionToken = currentUser.getSessionToken();

      try {
        const startIso = start.day ? convertDateStringToISO(start.day, start.time) : null;
        const endIso = end.day ? convertDateStringToISO(end.day, end.time) : null;

        // retrieve steps from each device
        const devices = await Parse.Cloud.run('allSteps', { patientId, start: startIso, end: endIso }, { sessionToken });
        this.devices = devices;

        // mertics calculation
        this.stepMetrics.numberSteps = calculateSum('steps', ...devices);
        this.stepMetrics.avgStepTime = calculateAverage('avgStepTime', ...devices);
        this.stepMetrics.cadence = calculateAverage('cadence', ...devices);
        this.stepMetrics.distance = calculateSum('distance', ...devices);

        // chart by hours
        if (getHoursBetween(new Date(startIso), new Date(endIso)) <= 48) {
          const { labels, datasets } = updateChartDataByHour(devices, startIso, endIso, 'steps');
          if (labels && datasets) {
            this.chartData = { labels, datasets };
          }
        } else {
          // chart by day
          const { labels, datasets } = updateChartDataByDay(devices, startIso, endIso, 'steps');

          if (labels && datasets) {
            this.chartData = { labels, datasets };
          }
        }
      } catch ({ code, message }) {
        this.error.code = code;
        this.error.message = message;
      }
    },
    updateChart(start, end, field = 'steps') {
      try {
        const startIso = start.day ? convertDateStringToISO(start.day, start.time) : null;
        const endIso = end.day ? convertDateStringToISO(end.day, end.time) : null;

        // chart by hours
        if (getHoursBetween(new Date(startIso), new Date(endIso)) <= 48) {
          const { labels, datasets } = updateChartDataByHour(this.devices, startIso, endIso, field);

          if (labels && datasets) {
            this.chartData = { labels, datasets };
          }
        } else {
          // chart by day
          const { labels, datasets } = updateChartDataByDay(this.devices, startIso, endIso, field);
          if (labels && datasets) {
            this.chartData = { labels, datasets };
          }
        }
      } catch ({ code, message }) {
        this.error.code = code;
        this.error.message = message;
      }
    },
    async updatePatient(patient) {
      const currentUser = Parse.User.current();
      if (!currentUser) {
        throw new Error('User not logged in');
      }

      const sessionToken = currentUser.getSessionToken();

      try {
        // retrieve patient by Id
        const response = await Parse.Cloud.run('updatePatientByDoctor', patient, { sessionToken });
        this.data = response;
        // show message that doctor saved succefully
        if (response) {
          this.app.raiseAlert(`Update patient ${this.data.firstname} succefully`, 'success');
        }
      } catch ({ code, message }) {
        this.error.code = code;
        this.error.message = message;
      }
    },
  },
});
